import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import type { UserRole, UserAnalyticsRow } from '../../lib/api';

const roleLabels: Record<UserRole, string> = {
  user: 'User',
  expert: 'Expert',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

const roleColors: Record<UserRole, string> = {
  super_admin: 'bg-purple-100 text-purple-800 border-purple-200',
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  expert: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  user: 'bg-gray-100 text-gray-800 border-gray-200',
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatRelative = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return formatDate(value);
};

const SORT_OPTIONS: { value: 'asc' | 'desc'; label: string }[] = [
  { value: 'asc', label: 'A–Z' },
  { value: 'desc', label: 'Z–A' },
];

const columns: ColumnDef<UserAnalyticsRow>[] = [
  {
    id: 'name',
    header: 'Name / Email',
    cell: ({ row }) => (
      <div className="text-left">
        <div className="font-medium text-gray-900">{row.original.name}</div>
        <div className="text-sm text-gray-500">{row.original.email || '—'}</div>
      </div>
    ),
  },
  {
    id: 'roles',
    header: 'Role',
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.roles.length === 0 && <span className="text-gray-400 italic text-sm">—</span>}
        {row.original.roles.map((role) => (
          <span
            key={role}
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${roleColors[role]}`}
          >
            {roleLabels[role]}
          </span>
        ))}
      </div>
    ),
  },
  {
    id: 'conversations',
    header: 'Conversations',
    accessorKey: 'conversations',
    cell: ({ getValue }) => <span className="text-gray-900">{getValue<number>()}</span>,
  },
  {
    id: 'messages',
    header: 'Messages',
    accessorKey: 'messages',
    cell: ({ getValue }) => <span className="text-gray-900">{getValue<number>()}</span>,
  },
  {
    id: 'lastActive',
    header: 'Last Active',
    accessorKey: 'lastActive',
    cell: ({ getValue }) => <span className="text-gray-900">{formatRelative(getValue<string | null>())}</span>,
  },
  {
    id: 'joinedAt',
    header: 'Joined',
    accessorKey: 'joinedAt',
    cell: ({ getValue }) => <span className="text-gray-900">{formatDate(getValue<string>())}</span>,
  },
];

/**
 * Per-user analytics table. Search, sort, and pagination are all server-driven
 * (see api.getUserAnalytics) so the page never has to pull the full user list
 * to the browser as the user base grows.
 */
export const UsersAnalyticsTable = ({
  rows,
  isLoading = false,
  search,
  onSearchChange,
  sortDir,
  onSortDirChange,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: {
  rows: UserAnalyticsRow[];
  isLoading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  sortDir: 'asc' | 'desc';
  onSortDirChange: (dir: 'asc' | 'desc') => void;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) => {
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">Users</h3>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative">
            <select
              value={sortDir}
              onChange={(e) => onSortDirChange(e.target.value as 'asc' | 'desc')}
              className="min-w-[110px] pl-3 pr-8 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  Sort: {opt.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                      No users match your search.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page</span>
              <div className="relative">
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="min-w-[70px] pl-3 pr-8 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
                >
                  {[10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm font-medium text-gray-900">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
